/**
 * `queryFn` factory that routes an endpoint to the active data source.
 *
 * Every sourced endpoint takes `{ source, params }`: the source is part of the
 * cache key, so `/test` (mock) and `/` (live) never share a cache entry. Errors
 * never throw out of a queryFn — they are normalised to `ApiError`, so
 * components branch on `error.kind` (`notConnected`, `unauthorized`, …).
 */
import type { BaseQueryApi, QueryReturnValue } from '@reduxjs/toolkit/query';

import type { DataSource, Requester, SourceCtx, SourceId } from '@/data-sources/contract';
import { loadSource } from '@/data-sources/load';
import { ApiErrorException, toApiError, type ApiError } from '@/lib/errors';

import { baseQuery, type AppBaseQuery } from './baseQuery';

export type Sourced<P> = { source: SourceId; params: P };

type BaseQueryArg = Parameters<AppBaseQuery>[0];
type BoundBaseQuery = (
  arg: BaseQueryArg,
) => ReturnType<AppBaseQuery> | Awaited<ReturnType<AppBaseQuery>>;

function requesterFrom(baseQuery: BoundBaseQuery): Requester {
  return async <T>(request: Parameters<Requester>[0]) => {
    const result = await baseQuery({
      url: request.url,
      method: request.method ?? 'GET',
      body: request.body,
      params: request.params,
    });
    if (result.error) throw new ApiErrorException(result.error);
    return result.data as T;
  };
}

/**
 * A requester outside RTK Query — for answer streams, which are not endpoints.
 * The same base query (auth header, refresh-on-401, envelope unwrap); `signal`
 * aborts the request in flight, which is how Stop reaches the server.
 */
export function streamRequester(
  store: Pick<BaseQueryApi, 'dispatch' | 'getState'>,
  signal: AbortSignal,
): Requester {
  return requesterFrom((arg) =>
    baseQuery(
      arg,
      {
        signal,
        abort: () => undefined,
        dispatch: store.dispatch,
        getState: store.getState,
        extra: undefined,
        endpoint: 'streamAnswer',
        type: 'mutation',
      },
      {},
    ),
  );
}

export function fromSource<P, R>(
  run: (source: DataSource, params: P, ctx: SourceCtx) => Promise<R>,
) {
  return async (
    arg: Sourced<P>,
    api: BaseQueryApi,
    _extraOptions: unknown,
    baseQuery: BoundBaseQuery,
  ): Promise<QueryReturnValue<R, ApiError, undefined>> => {
    try {
      const source = await loadSource(arg.source);
      const data = await run(source, arg.params, {
        signal: api.signal,
        request: requesterFrom(baseQuery),
      });
      return { data };
    } catch (error) {
      return { error: toApiError(error) };
    }
  };
}
